import { Box, CircularProgress, Typography, Collapse, IconButton, Fab, Button, Dialog, Slide } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import React from "react";
import { ChevronDown, ChevronUp, Sliders, SlidersHorizontal, Rows3, LayoutList, Infinity, Search, X, Calendar, Receipt, Plus, Upload } from "lucide-react";

import Toggle from "./ui/toggle";
import CheckboxTree from "./checkbox-tree";
import Dropdown from "./ui/dropdown";
import HeaderSearch from "./ui/header-search";
import AmountFilter, { AmountFilterHandle } from "./filters/amount-filter";
import DescriptionFilter, { DescriptionFilterHandle } from "./filters/description-filter";
import CategoryFilter, { CategoryFilterHandle } from "./filters/category-filter";
import DateFilter, { DateFilterHandle } from "./filters/date-filter";
import BankFilter, { BankFilterHandle } from "./filters/bank-filter";
import XeroSyncButton from "./xero-sync-button";
import { useSettings } from "../providers/settings-provider";
import { FilterParams } from "./transactions-table";

type FiltersMobileProps = {
    status: {
        cashflowMessage: string;
        state: 'good' | 'danger';
        dateInDanger: Date | null;
        daysBeforeNegative?: string;
        // See filters.tsx's matching field - true when the perf-diagnostic
        // "Cash Position" toggle has turned off the client-side calculation.
        disabled?: boolean;
    };
    unitValue: string;
    setUnitValue: (value: string) => void;
    startDate: Date;
    setStartDate: (value: Date) => void;
    endDate: Date;
    setEndDate: (value: Date) => void;
    // Needed by the combined sort/filter modal's embedded Date section
    // (view-by dropdown + manual date range toggle).
    viewBy?: string;
    setViewBy?: (value: string) => void;
    isDateRange?: boolean;
    setIsDateRange?: (value: boolean) => void;
    companyStatus?: {
        highestAmount?: number;
        lowestAmount?: number;
        earliestTransaction?: string | Date | null;
        latestTransaction?: string | Date | null;
    };
    isReal: boolean;
    setIsReal: (value: boolean) => void;
    isRecurringProvisions: boolean;
    setIsRecurringProvisions: (value: boolean) => void;
    isOneTimeProvisions: boolean;
    setIsOneTimeProvisions: (value: boolean) => void;
    bank: string[];
    setBank: (value: string[]) => void;
    category: string[];
    setCategory: (value: string[]) => void;
    handleCategoryChange: (value: string[]) => void;
    filterConfig: FilterParams | undefined;
    setFilterConfig: (value: FilterParams | undefined) => void;

    bankOptions: any[];
    categoryOptions: any[];
    onFiltersLoaded: () => void;
    appliedSim: { id: string; name: string } | null;
    setAppliedSim: (value: { id: string; name: string } | null) => void;
    onSelectProvision?: () => void;
    favoriteSimulations?: Array<{ id: string; name: string }>;
    // Toggles the row list between the detailed card layout and a compact,
    // denser table (date/description/amount/cash position only, no
    // checkboxes or action menus) - lets more rows fit on screen at once.
    compactView?: boolean;
    setCompactView?: (value: boolean) => void;
    // Quick Actions row inside the Sort & Filter sheet - replaces the old
    // floating "Today" Fab and "+" Fab (Import/Provision/Transaction menu),
    // which used to float over the mobile transaction list.
    onScrollToToday?: () => void;
    onImport?: () => void;
    onAddProvision?: () => void;
    onAddTransaction?: () => void;
    canImport?: boolean;
    canAddProvision?: boolean;
    canAddTransaction?: boolean;
};

// Slides the combined sort/filter modal up from the bottom like a native
// mobile sheet, instead of the default Dialog fade-in-place.
const SlideUpTransition = React.forwardRef(function SlideUpTransition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const danger = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 23 20" fill="none">
  <path d="M11.4993 0.340996C12.0947 0.340996 12.644 0.655471 12.9459 1.17121L22.0028 16.6015C22.3089 17.1214 22.3089 17.7629 22.0112 18.2829C21.7134 18.8028 21.1558 19.1257 20.5562 19.1257H2.44238C1.84278 19.1257 1.28511 18.8028 0.987405 18.2829C0.689702 17.7629 0.693895 17.1172 0.995791 16.6015L10.0527 1.17121C10.3546 0.655471 10.9039 0.340996 11.4993 0.340996ZM11.4993 5.70805C10.9416 5.70805 10.493 6.1567 10.493 6.71437V11.4105C10.493 11.9682 10.9416 12.4169 11.4993 12.4169C12.0569 12.4169 12.5056 11.9682 12.5056 11.4105V6.71437C12.5056 6.1567 12.0569 5.70805 11.4993 5.70805ZM12.841 15.1004C12.841 14.7445 12.6997 14.4032 12.448 14.1516C12.1964 13.9 11.8551 13.7586 11.4993 13.7586C11.1434 13.7586 10.8021 13.9 10.5505 14.1516C10.2989 14.4032 10.1575 14.7445 10.1575 15.1004C10.1575 15.4562 10.2989 15.7975 10.5505 16.0492C10.8021 16.3008 11.1434 16.4421 11.4993 16.4421C11.8551 16.4421 12.1964 16.3008 12.448 16.0492C12.6997 15.7975 12.841 15.4562 12.841 15.1004Z" fill="#A80000"/>
</svg>

const good = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 23 23" fill="none">
  <path d="M11.5 23C14.55 23 17.4751 21.7884 19.6317 19.6317C21.7884 17.4751 23 14.55 23 11.5C23 8.45001 21.7884 5.52494 19.6317 3.36827C17.4751 1.2116 14.55 0 11.5 0C8.45001 0 5.52494 1.2116 3.36827 3.36827C1.2116 5.52494 0 8.45001 0 11.5C0 14.55 1.2116 17.4751 3.36827 19.6317C5.52494 21.7884 8.45001 23 11.5 23ZM16.5762 9.38867L10.8262 15.1387C10.4039 15.5609 9.72109 15.5609 9.30332 15.1387L6.42832 12.2637C6.00605 11.8414 6.00605 11.1586 6.42832 10.7408C6.85059 10.323 7.5334 10.3186 7.95117 10.7408L10.0625 12.8521L15.0488 7.86133C15.4711 7.43906 16.1539 7.43906 16.5717 7.86133C16.9895 8.28359 16.9939 8.96641 16.5717 9.38418L16.5762 9.38867Z" fill="#0D6832"/>
</svg>;

const FiltersMobile: React.FC<FiltersMobileProps> = ({ 
    status,
    unitValue, 
    setUnitValue, 
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    viewBy,
    setViewBy,
    isDateRange,
    setIsDateRange,
    companyStatus,
    isReal,
    setIsReal, 
    isRecurringProvisions,
    setIsRecurringProvisions,
    isOneTimeProvisions,
    setIsOneTimeProvisions,
    bank,
    setBank,
    category,
    setCategory,
    handleCategoryChange,
    bankOptions,
    categoryOptions,
    filterConfig,
    setFilterConfig,
    onFiltersLoaded,
    appliedSim,
    setAppliedSim,
    onSelectProvision,
    favoriteSimulations = [],
    compactView = false,
    setCompactView,
    onScrollToToday,
    onImport,
    onAddProvision,
    onAddTransaction,
    canImport = true,
    canAddProvision = true,
    canAddTransaction = true,
}) => {
    const { companySettings, formatDate } = useSettings();
    const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set(['view', 'dates']));
    const [isFiltersExpanded, setIsFiltersExpanded] = React.useState(false);
    const [isSearchVisible, setIsSearchVisible] = React.useState(false);
    const [combinedFilterOpen, setCombinedFilterOpen] = React.useState(false);
    const currentSort = { sortBy: filterConfig?.sortBy || undefined, sortDirection: filterConfig?.sortDirection };

    // The combined modal has a single Apply/Clear pair instead of one per
    // section, so each embedded section's own onApply can't write straight
    // to filterConfig (5 synchronous calls in one click would each close
    // over the same stale filterConfig and clobber each other). Instead
    // they accumulate into this ref - a plain mutable value updates
    // synchronously, unlike queued setState calls - and the single Apply
    // button commits the fully-merged result once at the end.
    const draftFilterConfigRef = React.useRef<Partial<FilterParams>>({});
    const handleDraftApply = (params: Partial<FilterParams>) => {
        draftFilterConfigRef.current = { ...draftFilterConfigRef.current, ...params };
    };

    const dateFilterRef = React.useRef<DateFilterHandle>(null);
    const amountFilterRef = React.useRef<AmountFilterHandle>(null);
    const descriptionFilterRef = React.useRef<DescriptionFilterHandle>(null);
    const categoryFilterRef = React.useRef<CategoryFilterHandle>(null);
    const bankFilterRef = React.useRef<BankFilterHandle>(null);

    const handleApplyAllFilters = () => {
        draftFilterConfigRef.current = { ...(filterConfig || {}) };
        dateFilterRef.current?.applyNow();
        amountFilterRef.current?.applyNow();
        descriptionFilterRef.current?.applyNow();
        categoryFilterRef.current?.applyNow();
        bankFilterRef.current?.applyNow();
        setFilterConfig(draftFilterConfigRef.current as FilterParams);
        setCombinedFilterOpen(false);
    };

    const handleClearAllFilters = () => {
        draftFilterConfigRef.current = {};
        dateFilterRef.current?.clear();
        amountFilterRef.current?.clear();
        descriptionFilterRef.current?.clear();
        categoryFilterRef.current?.clear();
        bankFilterRef.current?.clear();
        setFilterConfig(draftFilterConfigRef.current as FilterParams);
    };

    const toggleSection = (section: string) => {
        const newSet = new Set(expandedSections);
        if (newSet.has(section)) {
            newSet.delete(section);
        } else {
            newSet.add(section);
        }
        setExpandedSections(newSet);
    };

    const calculateStartDateFromVisibleHistory = (): Date => {
        let start = new Date();
        if (companySettings && companySettings.settings && companySettings.settings.visibleHistory) {
            const visibleHistory = companySettings.settings.visibleHistory;
            const match = visibleHistory.match(/^(\d+)\s*(day|week|month|quarter|year|d|w|m|q|y)s?$/i);
            if (match) {
                const value = parseInt(match[1], 10);
                let unit = match[2].toLowerCase();

                if (unit.length === 1) {
                    switch (unit) {
                        case 'd': unit = 'day'; break;
                        case 'w': unit = 'week'; break;
                        case 'm': unit = 'month'; break;
                        case 'q': unit = 'quarter'; break;
                        case 'y': unit = 'year'; break;
                        default: break;
                    }
                }

                start = new Date();
                switch (unit) {
                    case 'day': start.setDate(start.getDate() - value); break;
                    case 'week': start.setDate(start.getDate() - value * 7); break;
                    case 'month': start.setMonth(start.getMonth() - value); break;
                    case 'quarter': start.setMonth(start.getMonth() - value * 3); break;
                    case 'year': start.setFullYear(start.getFullYear() - value); break;
                    default: break;
                }
            } else {
                start = new Date();
                start.setDate(start.getDate() - 7);
            }
        } else {
            start = new Date();
            start.setDate(start.getDate() - 7);
        }
        return start;
    };

    const calculateEndDate = (valueOverride?: string, startOverride?: Date): Date => {
        let valueStr = valueOverride || unitValue;
        const baseDate = startOverride || new Date();
        
        if (!valueStr && companySettings && companySettings.settings && companySettings.settings.projectionHorizon) {
            valueStr = companySettings.settings.projectionHorizon;
            const match = valueStr.match(/^(\d+)\s*(day|week|month|quarter|year)s?$/i);
            if (!match) return new Date();
            const value = parseInt(match[1], 10);
            const unit = match[2].toLowerCase();
            const now = new Date();
            switch (unit) {
                case 'day': now.setDate(now.getDate() + value); break;
                case 'week': now.setDate(now.getDate() + value * 7); break;
                case 'month': now.setMonth(now.getMonth() + value); break;
                case 'quarter': now.setMonth(now.getMonth() + value * 3); break;
                case 'year': now.setFullYear(now.getFullYear() + value); break;
                default: break;
            }
            return now;
        }
        if (!valueStr) return new Date();
        
        const compactMatch = valueStr.match(/^(\d+)([DWMQY])$/i);
        if (compactMatch) {
            const value = parseInt(compactMatch[1], 10);
            const unit = compactMatch[2].toUpperCase();
            const now = new Date(baseDate);

            switch (unit) {
                case 'D': now.setDate(now.getDate() + value); break;
                case 'W': now.setDate(now.getDate() + value * 7); break;
                case 'M': now.setMonth(now.getMonth() + value); break;
                case 'Q': now.setMonth(now.getMonth() + value * 3); break;
                case 'Y': now.setFullYear(now.getFullYear() + value); break;
                default: break;
            }
            return now;
        }
        
        const match = valueStr.match(/^(\d+)\s*(day|week|month|quarter|year)s?$/i);
        if (!match) return new Date();
        const value = parseInt(match[1], 10);
        const unit = match[2].toLowerCase();
        const now = new Date(baseDate);

        switch (unit) {
            case 'day': now.setDate(now.getDate() + value); break;
            case 'week': now.setDate(now.getDate() + value * 7); break;
            case 'month': now.setMonth(now.getMonth() + value); break;
            case 'quarter': now.setMonth(now.getMonth() + value * 3); break;
            case 'year': now.setFullYear(now.getFullYear() + value); break;
            default: break;
        }
        return now;
    };

    const FilterSection = ({ title, section, children }: { title: string; section: string; children: React.ReactNode }) => (
        <Box sx={{ mb: 1, border: '1px solid #e0ecf7', borderRadius: 1.5, overflow: 'hidden' }}>
            <Box
                onClick={() => toggleSection(section)}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    px: 1.5,
                    background: '#f5f9fe',
                    cursor: 'pointer',
                    '&:hover': { background: '#e8f4f0' }
                }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-default-text)' }}>{title}</Typography>
                {expandedSections.has(section) ? <ChevronUp size={18} color="var(--color-primary)" /> : <ChevronDown size={18} color="var(--color-primary)" />}
            </Box>
            <Collapse in={expandedSections.has(section)}>
                <Box sx={{ p: 1.5, background: 'var(--color-page-background)' }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', position: 'sticky', top: 72, zIndex: 2 }}>
            {/* Collapsed Header */}
            <Box sx={{ p: 1, background: 'var(--color-page-background)'}}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        minWidth: 0,
                        py: 1,
                        px: 1.5,
                        background: (status.disabled || !status.cashflowMessage) ? '#E8E8E8' : status.cashflowMessage.includes('never') ? 'rgba(var(--color-cashflow-positive-bg-rgb), 0.20)' : 'rgba(var(--color-cashflow-negative-bg-rgb), 0.1882)',
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.9 }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {status.disabled ? (
                            <Typography sx={{fontSize: 12, color: 'var(--color-text-muted)' }}>Cash position off</Typography>
                        ) : !status.cashflowMessage ? (
                            <>
                                <CircularProgress size={20} />
                                <Typography sx={{fontSize: 12, color: 'var(--color-text-muted)' }}>Loading...</Typography>
                            </>
                        ) : (
                            <>
                                {/* Same "days before you run out of money" countdown the
                                    desktop Filters bar shows in its own separate box - here
                                    it just takes over this icon's spot instead of adding a
                                    whole extra element to an already-narrow header. */}
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 'bold',
                                        lineHeight: 1,
                                        color: status.cashflowMessage.includes('never') ? 'var(--color-cashflow-positive-text)' : 'var(--color-cashflow-negative-text)',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {status.daysBeforeNegative === '∞'
                                        ? <Infinity size={18} />
                                        : status.daysBeforeNegative ?? (status.cashflowMessage.includes('never') ? good : danger)}
                                </Typography>
                                <Typography
                                    sx={{fontSize: 12, color: status.cashflowMessage.includes('never') ? 'var(--color-cashflow-positive-text)' : 'var(--color-cashflow-negative-text)' }}>
                                        {
                                            status.cashflowMessage.includes('|') 
                                                ? <>
                                                    {status.cashflowMessage.split('|')[0]}
                                                    <b> {formatDate(status.dateInDanger || new Date())}</b>
                                                </>
                                                : status.cashflowMessage
                                        }
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
                {/* Search/filter live outside the cashflow message box now -
                    they're page-level view controls, not part of that status
                    banner, so they shouldn't inherit its (sometimes red)
                    background or be visually read as part of the message.
                    Sync moved into the "+" FAB menu (Import/Provision/
                    Transaction) so it sits with the other page actions
                    instead of taking its own spot up here. The old main-filter
                    toggle button was removed - tapping the cashflow message
                    box itself already does the same thing. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {setCompactView && (
                        <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, cursor: 'pointer', width: 32, height: 32 }}
                            onClick={() => setCompactView(!compactView)}
                        >
                            {compactView ? <LayoutList size={20} color="var(--color-primary)" /> : <Rows3 size={20} color="var(--color-primary)" />}
                        </Box>
                    )}
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, cursor: 'pointer', width: 32, height: 32 }}
                        onClick={() => setCombinedFilterOpen(true)}
                    >
                        <SlidersHorizontal size={20} color="var(--color-primary)" />
                    </Box>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, cursor: 'pointer', width: 32, height: 32 }}
                        onClick={() => setIsSearchVisible(prev => !prev)}
                    >
                        <Search size={20} color="var(--color-primary)" />
                    </Box>
                </Box>
                </Box>
                {/* Same search-by-description box the desktop Filters bar has -
                    hidden by default (tapping the magnifying glass above
                    reveals it) so it doesn't permanently eat space in an
                    already-narrow header. */}
                <Collapse in={isSearchVisible} timeout="auto">
                    <Box sx={{ mt: 1 }}>
                        <HeaderSearch
                            value={filterConfig?.searchKeyword}
                            onSearch={(v) => setFilterConfig({ ...(filterConfig || {}), searchKeyword: v || undefined } as FilterParams)}
                            placeholder="Search transactions..."
                            width="100%"
                        />
                    </Box>
                </Collapse>
            </Box>

            {/* Expanded Filters */}
            <Collapse in={isFiltersExpanded} timeout="auto">
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', position: 'relative', background: '#f2f2f2' }}>
                    {/* Transaction Types */}
                    <FilterSection title="Show Transactions" section="types">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography sx={{ fontSize: 13, color: 'text.primary' }}>Real Transactions</Typography>
                                <Toggle checked={isReal} onChange={setIsReal} size="smaller"/>
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography sx={{ fontSize: 13, color: 'text.primary' }}>Recurring Provisions</Typography>
                                <Toggle checked={isRecurringProvisions} onChange={setIsRecurringProvisions} size="smaller"/>
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography sx={{ fontSize: 13, color: 'text.primary' }}>One-time Provisions</Typography>
                                <Toggle checked={isOneTimeProvisions} onChange={setIsOneTimeProvisions} size="smaller"/>
                            </Box>
                        </Box>
                    </FilterSection>

                    {/* Bank & Category - both a single dropdown apiece, so one
                        shared section header instead of two saves a whole
                        accordion row of vertical space. */}
                    <FilterSection title="Bank & Category" section="bankCategory">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <CheckboxTree
                                treeOptions={bankOptions}
                                value={bank}
                                onChange={(newBanks) => {
                                    const processedBanks = newBanks.filter(id => !['select-all-banks'].includes(id));
                                    setBank(processedBanks);
                                }}
                                placeholder="Select Banks"
                                countLabel="banks"
                            />
                            <CheckboxTree
                                treeOptions={categoryOptions}
                                value={category}
                                onChange={handleCategoryChange}
                                placeholder="Select Categories"
                                countLabel="categories"
                            />
                        </Box>
                    </FilterSection>

                    {/* Simulation */}
                    <Box sx={{ p: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button size="small" sx={{ fontSize: 10, height: 25, textWrap: 'nowrap', flex: 1, minWidth: 0 }} variant="outlined" onClick={onSelectProvision}>{appliedSim?.name || 'Select Simulation'}</Button>
                            <Button size="small" sx={{ fontSize: 10, height: 25, textWrap: 'nowrap', flex: 1, minWidth: 0 }} variant="outlined" onClick={() => setAppliedSim(null)} disabled={!appliedSim}>Clear Simulation</Button>
                        </Box>
                        {favoriteSimulations.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {favoriteSimulations.map(sim => (
                                    <Button
                                        key={sim.id}
                                        size="small"
                                        sx={{
                                            fontSize: 10, height: 25, textWrap: 'nowrap',
                                            borderColor: appliedSim?.id !== sim.id ? '#bdbdbd' : 'default',
                                            color: appliedSim?.id !== sim.id ? '#737373' : '#fff',
                                            fontWeight: appliedSim?.id !== sim.id ? 'normal' : 'bold',
                                        }}
                                        variant={appliedSim?.id !== sim.id ? 'outlined' : 'contained'}
                                        onClick={() => setAppliedSim({ id: sim.id, name: sim.name })}
                                    >
                                        {sim.name}
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Floating Action Button */}
                    <Fab
                        onClick={() => setIsFiltersExpanded(false)}
                        sx={{
                            position: 'fixed',
                            bottom: 20,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'var(--color-primary)',
                            color: '#fff',
                            '&:hover': {
                                background: 'var(--color-primary)'
                            },
                            zIndex: 1000
                        }}
                    >
                        <ChevronUp size={24} />
                    </Fab>
                </Box>
            </Collapse>

            {/* Combined sort/filter modal - replaces the old horizontal chip
                row of separately-anchored popovers (Date/Amount/Description/
                Category/Bank) with one centered dialog so it doesn't
                require 5 individual taps + scrolling to reach each one. */}
            <Dialog
                open={combinedFilterOpen}
                onClose={() => setCombinedFilterOpen(false)}
                fullWidth
                TransitionComponent={SlideUpTransition}
                sx={{ '& .MuiDialog-container': { alignItems: 'flex-end' } }}
                PaperProps={{
                    sx: {
                        m: 0,
                        width: '100%',
                        maxWidth: '100%',
                        maxHeight: '88vh',
                        height: '88vh',
                        borderRadius: '16px 16px 0 0',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1, flexShrink: 0, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort & Filter</Typography>
                    <IconButton size="small" onClick={() => setCombinedFilterOpen(false)}>
                        <X size={20} />
                    </IconButton>
                </Box>
                <Box sx={{ px: 2, py: 2, overflowY: 'auto', flex: 1 }}>
                    {(onScrollToToday || onAddTransaction || onAddProvision || onImport) && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                                Quick Actions
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {onScrollToToday && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Calendar size={16} />}
                                        onClick={() => { onScrollToToday(); setCombinedFilterOpen(false); }}
                                        sx={{ flex: '1 1 auto', minWidth: 140, fontSize: 12, textWrap: 'nowrap' }}
                                    >
                                        Jump to Today
                                    </Button>
                                )}
                                {onAddTransaction && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!canAddTransaction}
                                        startIcon={<Receipt size={16} />}
                                        onClick={() => { onAddTransaction(); setCombinedFilterOpen(false); }}
                                        sx={{ flex: '1 1 auto', minWidth: 140, fontSize: 12, textWrap: 'nowrap' }}
                                    >
                                        Add Transaction
                                    </Button>
                                )}
                                {onAddProvision && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!canAddProvision}
                                        startIcon={<Plus size={16} />}
                                        onClick={() => { onAddProvision(); setCombinedFilterOpen(false); }}
                                        sx={{ flex: '1 1 auto', minWidth: 140, fontSize: 12, textWrap: 'nowrap' }}
                                    >
                                        Add Provision
                                    </Button>
                                )}
                                {onImport && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!canImport}
                                        startIcon={<Upload size={16} />}
                                        onClick={() => { onImport(); setCombinedFilterOpen(false); }}
                                        sx={{ flex: '1 1 auto', minWidth: 140, fontSize: 12, textWrap: 'nowrap' }}
                                    >
                                        Import
                                    </Button>
                                )}
                                <XeroSyncButton type="transactions" onClick={() => setCombinedFilterOpen(false)} />
                            </Box>
                        </Box>
                    )}
                    <FilterSection title="Date" section="cf-date">
                        <DateFilter
                            ref={dateFilterRef}
                            embedded
                            hideActions
                            onApply={handleDraftApply}
                            currentSort={currentSort}
                            viewBy={viewBy}
                            setViewBy={setViewBy}
                            unitValue={unitValue}
                            setUnitValue={setUnitValue}
                            isDateRange={isDateRange}
                            setIsDateRange={setIsDateRange}
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            companyStatus={companyStatus}
                        />
                    </FilterSection>
                    <FilterSection title="Amount" section="cf-amount">
                        <AmountFilter
                            ref={amountFilterRef}
                            embedded
                            hideActions
                            onApply={handleDraftApply}
                            currentSort={currentSort}
                            min={companyStatus?.lowestAmount ?? 0}
                            max={companyStatus?.highestAmount ?? 999999}
                            currentAmountKeyword={filterConfig?.amountKeyword}
                        />
                    </FilterSection>
                    <FilterSection title="Description" section="cf-description">
                        <DescriptionFilter
                            ref={descriptionFilterRef}
                            embedded
                            hideActions
                            onApply={handleDraftApply}
                            currentSort={currentSort}
                            currentDescriptionKeyword={filterConfig?.descriptionKeyword}
                        />
                    </FilterSection>
                    <FilterSection title="Category" section="cf-category">
                        <CategoryFilter
                            ref={categoryFilterRef}
                            embedded
                            hideActions
                            onApply={handleDraftApply}
                            currentSort={currentSort}
                            categoryOptions={categoryOptions}
                        />
                    </FilterSection>
                    <FilterSection title="Bank" section="cf-bank">
                        <BankFilter
                            ref={bankFilterRef}
                            embedded
                            hideActions
                            onApply={handleDraftApply}
                            currentSort={currentSort}
                        />
                    </FilterSection>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 1.5, flexShrink: 0, borderTop: '1px solid #eee' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-primary)' } }}
                        onClick={handleApplyAllFilters}
                    >
                        Apply Now
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-container-background)' } }}
                        onClick={handleClearAllFilters}
                    >
                        Clear
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export { FiltersMobile };
export type { FiltersMobileProps };
